import React, { useState, useEffect, useContext, useMemo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { lineasParaSeguir, CANAL } from "../../helpers/lineasParaSeguir";


const SugerenciaOtraLinea = ({ ticket, onCambiado }) => {
  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();

  const [estado, setEstado] = useState(null);
  const [cambiando, setCambiando] = useState(null);

  const [menu, setMenu] = useState(null);

  const esWebChat = ticket?.channel === "webchat";

  useEffect(() => {
    if (!esWebChat || !ticket?.id) {
      setEstado(null);
      return undefined;
    }

    let vivo = true;

    (async () => {
      try {
        const { data } = await api.get(`/tickets/${ticket.id}/visitante-web`);
        if (vivo) setEstado(data);
      } catch {
        if (vivo) setEstado(null);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [esWebChat, ticket?.id, ticket?.updatedAt]);

  const misColas = useMemo(
    () => (user?.queues || []).map((q) => q.id),
    [user?.queues]
  );

  const lineas = useMemo(
    () =>
      lineasParaSeguir({
        whatsApps,
        misColas,
        telefono: estado?.telefono,
        actualId: ticket?.whatsappId,
        queueId: ticket?.queueId
      }),
    [whatsApps, misColas, estado?.telefono, ticket?.whatsappId, ticket?.queueId]
  );

  const porCanal = useMemo(() => {
    const grupos = [];

    lineas.forEach((linea) => {
      const grupo = grupos.find((g) => g.canal === linea.channel);

      if (grupo) grupo.lineas.push(linea);
      else grupos.push({ canal: linea.channel, lineas: [linea] });
    });

    return grupos;
  }, [lineas]);

  const cambiar = async (linea) => {
    setMenu(null);
    setCambiando(linea.id);

    try {
      await api.put(`/tickets/${ticket.id}`, { whatsappId: linea.id });

      if (typeof onCambiado === "function") onCambiado(linea);
    } catch (err) {
      toastError(err);
    }

    setCambiando(null);
  };

  if (!esWebChat || !estado?.seFue || !porCanal.length) return null;

  const abiertas =
    porCanal.find((g) => g.canal === menu?.canal)?.lineas || [];

  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        padding: "8px 12px",
        borderTop: "1px solid rgba(128,128,128,0.2)"
      }}
    >
      <SwapHorizIcon style={{ fontSize: 18, opacity: 0.7 }} />

      {}
      <Typography style={{ fontSize: 13, marginRight: 4 }}>
        {estado.motivo === "cerro"
          ? "El visitante cerró el chat. Para seguir escribiéndole:"
          : "El visitante lleva un rato sin responder. Si quieres seguirle por otro lado:"}
      </Typography>

      {porCanal.map(({ canal, lineas: suyas }) => {
        const pinta = CANAL[canal] || { nombre: canal, color: "#607d8b" };

        const unaSola = suyas.length === 1;

        const ocupado = cambiando !== null;
        const esElQueVa = suyas.some((l) => l.id === cambiando);

        return (
          <Button
            key={canal}
            size="small"
            variant="contained"
            disableElevation
            disabled={ocupado}
            onClick={(e) =>
              unaSola
                ? cambiar(suyas[0])
                : setMenu({ canal, ancla: e.currentTarget })
            }
            startIcon={
              ocupado && esElQueVa ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                <WhatsAppIcon />
              )
            }
            endIcon={unaSola ? null : <ArrowDropDownIcon />}
            style={{
              backgroundColor: pinta.color,
              color: "#fff",
              opacity: ocupado && !esElQueVa ? 0.45 : 1,
              textTransform: "none",
              textAlign: "left",
              lineHeight: 1.15,
              padding: "6px 14px"
            }}
          >
            <span style={{ display: "block" }}>
              <span style={{ display: "block", fontWeight: 600 }}>
                Abrir con {pinta.nombre}
              </span>

              {}
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 400,
                  opacity: 0.85
                }}
              >
                {unaSola ? suyas[0].name : `${suyas.length} conexiones`}
              </span>
            </span>
          </Button>
        );
      })}

      <Menu
        open={Boolean(menu)}
        anchorEl={menu?.ancla}
        onClose={() => setMenu(null)}
      >
        {abiertas.map((linea) => {
          const mismaCola =
            Boolean(ticket?.queueId) &&
            (linea.queues || []).some((q) => q.id === ticket.queueId);

          return (
            <MenuItem key={linea.id} onClick={() => cambiar(linea)}>
              {}
              <ListItemText
                primary={linea.name}
                secondary={mismaCola ? "Mismo departamento que este chat" : null}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default SugerenciaOtraLinea;
